import { useEffect, useMemo, useState } from 'react';

import BpkButton, {
  BUTTON_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-button';
import {
  withRtlSupport,
  withButtonAlignment,
} from '@skyscanner-internal/backpack-web/bpk-component-icon';
import ChevronLeft from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/chevron-left';

import FlightInfoTable from './FlightInfoTable';
import FlightStatusHeader from './FlightStatusHeader';
import { FlightStatusMultipleFlights } from './FlightStatusMultipleFlights';
import {
  getAirportOffsetMs,
  getFlightTimelineTime,
  getLandingTime,
  getLocalisedScheduledDeparture,
  getSelectedFlight,
  selectDefaultFlight,
  sortFlightsByTime,
} from './flightSelection';
import { getFlightDateKey } from './flightStatusUpdate';
import {
  createDateFromParts,
  formatBrowserLocalDateKey,
  formatDateKey,
  padNumber,
  parseDateKeyToUTCDate,
} from './utils';

import type { NetSkyscannerArrivaldeparturesvcV1ArrivalDepartureService as ArrivalDepartureTypes } from '@skyscanner-internal/arrival-departure-svc-web-http-client';
import type { FlightStatusDetailsProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/FlightStatusDetailsProps';

import STYLES from './FlightStatusDetails.module.scss';

type FlightStatusUpdate = ArrivalDepartureTypes.FlightStatusUpdate;

const hashPattern = /^#(\d{4}-\d{2}-\d{2})(?:_(\d{2})-(\d{2}))?$/;
const AlignedChevronLeft = withButtonAlignment(withRtlSupport(ChevronLeft));

type HashSelection = {
  effectiveDateTime: Date;
  selectedDate: string;
  hasExplicitTime: boolean;
};

const formatTimeKey = (date: Date) =>
  `${padNumber(date.getUTCHours())}-${padNumber(date.getUTCMinutes())}`;

/**
 * Build the `#YYYY-MM-DD_HH-mm` hash from a Date. The Date is expected to
 * carry the flight's departure-airport-local wall clock in its UTC parts
 * (see `getLocalisedScheduledDeparture`). The resulting hash therefore
 * encodes airport-local scheduled-departure time, not UTC.
 *
 * Why airport-local and not UTC: airport TZ is a property of the flight,
 * not the viewer, so the hash is still stable and shareable across users.
 * And the hash then matches the Scheduled time row (UAT 3.1) the viewer
 * reads on the page.
 */
const buildHashFromDateTime = (date: Date) =>
  `#${formatDateKey(date)}_${formatTimeKey(date)}`;

const getHashSelection = (fallbackNow: Date): HashSelection => {
  const browserLocalTodayKey = formatBrowserLocalDateKey(fallbackNow);

  if (typeof globalThis.window === 'undefined') {
    return {
      effectiveDateTime: fallbackNow,
      selectedDate: browserLocalTodayKey,
      hasExplicitTime: false,
    };
  }

  const match = hashPattern.exec(globalThis.window.location.hash);

  if (!match) {
    return {
      effectiveDateTime: fallbackNow,
      selectedDate: browserLocalTodayKey,
      hasExplicitTime: false,
    };
  }

  const [, datePart, hoursPart, minutesPart] = match;
  const [year, month, day] = datePart.split('-').map(Number);
  const hours = hoursPart ? Number(hoursPart) : fallbackNow.getUTCHours();
  const minutes = minutesPart
    ? Number(minutesPart)
    : fallbackNow.getUTCMinutes();

  const parsed = createDateFromParts(year, month, day, hours, minutes);

  if (!parsed) {
    return {
      effectiveDateTime: fallbackNow,
      selectedDate: browserLocalTodayKey,
      hasExplicitTime: false,
    };
  }

  // Hash date is now airport-local (same frame as `getFlightDateKey`), so it
  // maps directly to a chip key without any cross-frame resolution.
  return {
    effectiveDateTime: parsed,
    selectedDate: datePart,
    hasExplicitTime: Boolean(hoursPart && minutesPart),
  };
};

const getVisibleDateKeys = (flights: FlightStatusUpdate[]) =>
  Array.from(
    new Set(
      flights
        .map((flight) => getFlightDateKey(flight))
        .filter((dateKey) => dateKey.length > 0),
    ),
  ).sort((a, b) => a.localeCompare(b));

const getHashDateTimeForFlight = (
  flight: FlightStatusUpdate,
  selectedDateKey: string,
  fallbackDateTime: Date,
) => {
  const selectedDate = parseDateKeyToUTCDate(selectedDateKey);

  if (!selectedDate) {
    return fallbackDateTime;
  }

  // Prefer the flight's airport-local scheduled departure so the resulting
  // Date's UTC parts carry the airport-local wall clock that the hash will
  // encode. Falls back to UTC scheduled departure, and then to
  // `getFlightTimelineTime` / `getLandingTime` when no departure at all.
  const flightTime =
    getLocalisedScheduledDeparture(flight) ||
    getFlightTimelineTime(flight) ||
    getLandingTime(flight);
  const hours = flightTime
    ? flightTime.getUTCHours()
    : fallbackDateTime.getUTCHours();
  const minutes = flightTime
    ? flightTime.getUTCMinutes()
    : fallbackDateTime.getUTCMinutes();

  const selectionDateTime = createDateFromParts(
    selectedDate.getUTCFullYear(),
    selectedDate.getUTCMonth() + 1,
    selectedDate.getUTCDate(),
    hours,
    minutes,
  );

  return selectionDateTime || fallbackDateTime;
};

/**
 * FlightStatusDetails: Main container and source of truth for active flight selection.
 *
 * Sub-component communication:
 * - Sub-components receive the activeFlight and strings as props
 * - Tab/Date navigation: fires onDateChange(date: Date) event → parent updates hash + recalculates selectedFlight
 * - Same-day flight selection: fires onFlightSelect(flight: FlightStatusUpdate) event → parent updates hash + sets that flight as active
 * - Both events update window.location.hash for deep-linking support
 *
 * Timezone handling:
 * - Backend returns UTC times (scheduledDepartureTime, scheduledArrivalTime, etc. in ISO format)
 *   plus airport-local wall-clock times with no TZ suffix (localisedScheduledDepartureTime, ...)
 * - URL fragments use *departure-airport-local* wall-clock times (YYYY-MM-DD_HH-mm). The airport
 *   TZ is a property of the flight, not the viewer, so the hash stays stable and shareable.
 * - Date-chip keys (`getFlightDateKey`) are also departure-airport-local, so the hash date maps
 *   directly onto a chip without cross-frame resolution.
 * - Displayed departure times are formatted verbatim from localised fields (UAT 3.1); arrival
 *   times are formatted verbatim from arrival-localised fields (UAT 3.2).
 * - The default-selected chip on first load uses *browser-local* today (UAT 10.1), the only
 *   browser-TZ reference on the page.
 * - Active-window / next-upcoming comparisons use real UTC (`scheduled*Time`), with the hash
 *   translated back to UTC via the airport offset from any flight that exposes the pair.
 */
const FlightStatusDetails = ({
  flights,
  locale,
  requestedCarrierCode,
  requestedFlightNumber,
  strings,
  title,
}: FlightStatusDetailsProps) => {
  const requestedFlightCode =
    `${requestedCarrierCode}${requestedFlightNumber}`.toUpperCase();

  const [hashSelection, setHashSelection] = useState<HashSelection>(() => {
    // Intentionally do NOT parse the hash here.
    // The server has no window and falls back to current time, so the client must
    // do the same to avoid a React hydration mismatch on the initial render.
    // Hash-based selection is applied in the useEffect below after mount.
    const now = new Date();
    return {
      effectiveDateTime: now,
      selectedDate: formatBrowserLocalDateKey(now),
      hasExplicitTime: false,
    };
  });

  useEffect(() => {
    if (typeof globalThis.window === 'undefined') {
      return undefined;
    }

    // Sync selection from hash after mount. This is the authoritative source for
    // chip selection - the server is irrelevant here.
    // If the hash is present but invalid, remove it so the URL stays clean.
    // Use replaceState to strip the '#' itself, not just the fragment value.
    const currentHash = globalThis.window.location.hash;
    if (currentHash && !hashPattern.test(currentHash)) {
      globalThis.window.history.replaceState(
        null,
        '',
        globalThis.window.location.pathname + globalThis.window.location.search,
      );
    }
    setHashSelection(getHashSelection(new Date()));

    const syncSelectionFromHash = () => {
      setHashSelection(getHashSelection(new Date()));
    };

    globalThis.window.addEventListener('hashchange', syncSelectionFromHash);

    return () => {
      globalThis.window.removeEventListener(
        'hashchange',
        syncSelectionFromHash,
      );
    };
  }, []);

  const visibleDateKeys = useMemo(() => getVisibleDateKeys(flights), [flights]);
  const hasHashSelectedDate = useMemo(
    () => visibleDateKeys.includes(hashSelection.selectedDate),
    [visibleDateKeys, hashSelection.selectedDate],
  );
  const airportOffsetMs = useMemo(() => getAirportOffsetMs(flights), [flights]);

  const hashSelectedDateFlights = useMemo(
    () =>
      sortFlightsByTime(
        flights.filter(
          (flight) => getFlightDateKey(flight) === hashSelection.selectedDate,
        ),
      ),
    [flights, hashSelection.selectedDate],
  );

  const fallbackDateKey = visibleDateKeys[0];

  const scopedDateKey = hasHashSelectedDate
    ? hashSelection.selectedDate
    : fallbackDateKey || hashSelection.selectedDate;

  const scopedDateFlights = useMemo(
    () =>
      scopedDateKey === hashSelection.selectedDate
        ? hashSelectedDateFlights
        : sortFlightsByTime(
            flights.filter(
              (flight) => getFlightDateKey(flight) === scopedDateKey,
            ),
          ),
    [
      flights,
      hashSelectedDateFlights,
      hashSelection.selectedDate,
      scopedDateKey,
    ],
  );

  useEffect(() => {
    if (typeof globalThis.window === 'undefined') {
      return;
    }

    if (flights.length <= 1) {
      return;
    }

    if (flights.length === 0 || hasHashSelectedDate || !fallbackDateKey) {
      return;
    }

    // Hash pointed at a date that has no flights. Pick the first flight on
    // the earliest visible day and encode *its* scheduled departure into
    // the hash, so the hash cleanly identifies a real flight instance
    // rather than an arbitrary "now" moment (under the airport-local hash
    // model, "now" in real UTC does not translate cleanly onto an airport-
    // local wall clock without picking a flight anyway).
    const fallbackDateFlights = sortFlightsByTime(
      flights.filter((flight) => getFlightDateKey(flight) === fallbackDateKey),
    );
    const firstFlightOnFallback = fallbackDateFlights[0];

    if (!firstFlightOnFallback) {
      return;
    }

    const fallbackDateTime = getHashDateTimeForFlight(
      firstFlightOnFallback,
      fallbackDateKey,
      new Date(),
    );

    if (!fallbackDateTime) {
      return;
    }

    const nextHash = buildHashFromDateTime(fallbackDateTime);

    if (globalThis.window.location.hash !== nextHash) {
      globalThis.window.history.replaceState(
        globalThis.window.history.state,
        '',
        globalThis.window.location.pathname +
          globalThis.window.location.search +
          nextHash,
      );
    }

    setHashSelection({
      effectiveDateTime: fallbackDateTime,
      selectedDate: fallbackDateKey,
      hasExplicitTime: true,
    });
  }, [fallbackDateKey, flights, flights.length, hasHashSelectedDate]);

  // CASTLE-5111: hand selectDefaultFlight a single date's flights
  // (scopedDateKey), never the whole list, so an active flight cannot lose
  // to a future instance on another date.
  const selectedFlightCandidate = useMemo(
    () =>
      hashSelection.hasExplicitTime
        ? getSelectedFlight(
            scopedDateFlights,
            hashSelection.effectiveDateTime,
            true,
            airportOffsetMs,
          )
        : selectDefaultFlight(
            scopedDateFlights,
            hashSelection.effectiveDateTime,
          ),
    [
      hashSelection.effectiveDateTime,
      hashSelection.hasExplicitTime,
      scopedDateFlights,
      airportOffsetMs,
    ],
  );

  // Both selectors only ever draw from scopedDateFlights, and every flight
  // there satisfies getFlightDateKey === scopedDateKey by construction, so the
  // displayed date tab is always the scoped date. (Pre-CASTLE-5111 the default
  // selector saw every date's flights, so the shown date had to be derived
  // from whichever flight it picked; scoping the input to one date made that
  // derivation - and a separate selectedDateFlights memo - redundant.)
  const effectiveDateKey = scopedDateKey;
  const selectedDateFlights = scopedDateFlights;

  // Explicit selection priority: preferred (hash or window match) → first on selected date → first overall.
  // Contract: when flights[] is non-empty, this fallback chain guarantees selectedFlight is non-null.
  // Reasoning:
  // - selectedFlightCandidate covers the normal selection path
  // - selectedDateFlights[0] covers cases where the chosen date has flights but no rule matched
  // - flights[0] covers malformed rows where date-key derivation leaves selectedDateFlights empty
  const selectedFlight =
    selectedFlightCandidate ?? selectedDateFlights[0] ?? flights[0] ?? null;

  const selectedFlightIndex = useMemo(() => {
    const selectedFlightId = selectedFlight?.scheduleInstanceKey ?? '';
    const selectedIndex = selectedDateFlights.findIndex(
      (flight) => flight.scheduleInstanceKey === selectedFlightId,
    );

    return Math.max(selectedIndex, 0);
  }, [selectedDateFlights, selectedFlight]);

  const hasFlightSelector = flights.length > 1 && visibleDateKeys.length > 0;

  /**
   * Update URL fragment and hash selection state when date or flight changes.
   * Called from:
   * - Tab date click (onDateChange event from future FlightStatusDetailHeader)
   * - Same-day flight list click (onFlightSelect event from future FlightSelector)
   */
  const updateHash = (nextDateTime: Date) => {
    if (typeof globalThis.window === 'undefined') {
      return;
    }

    if (flights.length <= 1) {
      return;
    }

    const nextHash = buildHashFromDateTime(nextDateTime);

    if (globalThis.window.location.hash !== nextHash) {
      globalThis.window.history.replaceState(
        globalThis.window.history.state,
        '',
        globalThis.window.location.pathname +
          globalThis.window.location.search +
          nextHash,
      );
    }
    setHashSelection({
      effectiveDateTime: nextDateTime,
      selectedDate: formatDateKey(nextDateTime),
      hasExplicitTime: true,
    });
  };

  const handleDateSelect = (dateKey: string) => {
    const dateFlights = sortFlightsByTime(
      flights.filter((flight) => getFlightDateKey(flight) === dateKey),
    );
    const firstFlightOnDate = dateFlights[0];

    if (!firstFlightOnDate) {
      return;
    }

    const nextDateTime = getHashDateTimeForFlight(
      firstFlightOnDate,
      dateKey,
      hashSelection.effectiveDateTime,
    );

    if (nextDateTime) {
      updateHash(nextDateTime);
    }
  };

  const handleFlightSelect = (flightIndex: number) => {
    const selectedByIndex = selectedDateFlights[flightIndex];

    if (!selectedByIndex) {
      return;
    }

    const selectionDateTime = getHashDateTimeForFlight(
      selectedByIndex,
      effectiveDateKey,
      hashSelection.effectiveDateTime,
    );

    updateHash(selectionDateTime);
  };

  const renderBackToTrackerButton = () => (
    <div className={STYLES.FlightStatusDetails__backButtonWrap}>
      <BpkButton href="/flight-tracker" type={BUTTON_TYPES.secondary}>
        <AlignedChevronLeft /> {strings.FlightTrackerFlight_cta_track_another}
      </BpkButton>
    </div>
  );

  if (flights.length === 0) {
    return (
      <div
        className={STYLES.FlightStatusDetails}
        data-testid="flight-status-details-empty"
      >
        <FlightStatusHeader
          title={title}
          carrierCode={requestedCarrierCode}
          selectedFlight={null}
          strings={strings}
          locale={locale}
        />
      </div>
    );
  }

  // Contract enforcement: if flights exist, selectedFlight must be non-null
  // This guard should never execute, but catches any logic bugs in the fallback chain above
  if (!selectedFlight) {
    return null;
  }

  return (
    <div
      className={STYLES.FlightStatusDetails}
      data-testid="flight-status-details"
    >
      <FlightStatusHeader
        title={title}
        carrierCode={requestedCarrierCode}
        selectedFlight={selectedFlight}
        strings={strings}
        locale={locale}
      />

      {hasFlightSelector && (
        <FlightStatusMultipleFlights
          selectedDateFlights={selectedDateFlights}
          selectedFlight={selectedFlight}
          selectedFlightIndex={selectedFlightIndex}
          visibleDateKeys={visibleDateKeys}
          effectiveDateKey={effectiveDateKey}
          requestedFlightCode={requestedFlightCode}
          locale={locale}
          strings={strings}
          onDateSelect={handleDateSelect}
          onFlightSelect={handleFlightSelect}
        />
      )}

      <FlightInfoTable
        locale={locale}
        flight={selectedFlight}
        strings={strings}
      />

      {renderBackToTrackerButton()}
    </div>
  );
};

export default FlightStatusDetails;
